/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { InterviewCreate } from './interview-create'
import { InterviewActivityTaskRes } from './interview-activity-task-res'
import { InterviewUpdate } from './interview-update'
import { TaskReflection } from './task-reflection'
import { InterviewSubmitReq } from './interview-submit-req'
import { LoadInterviewActivityTaskQuery } from './load-interview-activity-task-query'
import { QueryInterviewReflectionsQuery } from './query-interview-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const interviewApi = {
  createInterviewActivityTaskConfig(
    interviewCreate: InterviewCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/interview/activity-tasks',
      {},
      null,
      interviewCreate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          paperId: (v: any) => v || 0,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          description: (v: any) => v || '',
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】面试-创建活动任务
   * @param interviewCreate
   * @param [options]
   * @return AxiosResponsePromise<object>
   */
  createInterviewActivityTask(
    interviewCreate: InterviewCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<object> {
    return axios(
      interviewApi.createInterviewActivityTaskConfig(interviewCreate, options)
    ) as any
  },

  useCreateInterviewActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: InterviewCreate) =>
        interviewApi.createInterviewActivityTask(payload),
      mutationOptions
    )
  },

  loadInterviewActivityTaskConfig(
    id: number,
    loadInterviewActivityTaskQuery?: LoadInterviewActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/interview/activity-tasks/{id}',
      {
        id,
      },
      loadInterviewActivityTaskQuery
    )
  },

  /**
   * @summary 【后台】面试-详情
   * @param id
   * @param [loadInterviewActivityTaskQuery]
   * @param [loadInterviewActivityTaskQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<InterviewActivityTaskRes>
   */
  loadInterviewActivityTask<TQuery extends LoadInterviewActivityTaskQuery>(
    id: number,
    loadInterviewActivityTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InterviewActivityTaskRes, TQuery['expand']>> {
    return axios(
      interviewApi.loadInterviewActivityTaskConfig(
        id,
        loadInterviewActivityTaskQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new InterviewActivityTaskRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadInterviewActivityTask<TQuery extends LoadInterviewActivityTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InterviewActivityTaskRes, TQuery['expand']>> {
    return useQueryApi<Expand<InterviewActivityTaskRes, TQuery['expand']>>(
      interviewApi.loadInterviewActivityTask,
      'Interview.loadInterviewActivityTask',
      queryOptions,
      id,
      query
    )
  },

  updateInterviewActivityTaskConfig(
    id: number,
    interviewUpdate: InterviewUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/interview/activity-tasks/{id}',
      {
        id,
      },
      null,
      interviewUpdate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          paperId: (v: any) => v || 0,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          description: (v: any) => v || '',
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】面试-编辑活动任务
   * @param id
   * @param interviewUpdate
   * @param [options]
   * @return AxiosResponsePromise<object>
   */
  updateInterviewActivityTask(
    id: number,
    interviewUpdate: InterviewUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<object> {
    return axios(
      interviewApi.updateInterviewActivityTaskConfig(
        id,
        interviewUpdate,
        options
      )
    ) as any
  },

  useUpdateInterviewActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: InterviewUpdate }) =>
        interviewApi.updateInterviewActivityTask(id, payload),
      mutationOptions
    )
  },

  queryInterviewReflectionsConfig(
    id: number,
    queryInterviewReflectionsQuery?: QueryInterviewReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/interview/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryInterviewReflectionsQuery
    )
  },

  /**
   * @summary 【后台】问卷任务-问卷数据-任务人员列表(完成情况)
   * @param id
   * @param [queryInterviewReflectionsQuery]
   * @param [queryInterviewReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryInterviewReflectionsQuery.reflection.nameIn]
   * @param [queryInterviewReflectionsQuery.reflection.nameLike]
   * @param [queryInterviewReflectionsQuery.reflection.accountLike]
   * @param [queryInterviewReflectionsQuery.reflection.accountIn]
   * @param [queryInterviewReflectionsQuery.reflection.genderIn]
   * @param [queryInterviewReflectionsQuery.reflection.statusIn]
   * @param [queryInterviewReflectionsQuery.reflection.phoneIn]
   * @param [queryInterviewReflectionsQuery.reflection.phoneLike]
   * @param [queryInterviewReflectionsQuery.reflection.emailIn]
   * @param [queryInterviewReflectionsQuery.reflection.emailLike]
   * @param [queryInterviewReflectionsQuery.interview.interviewResultIdIn] 面试结果
   * @param [queryInterviewReflectionsQuery.interview.interviewerNameLike] 面试官名
   * @param [queryInterviewReflectionsQuery.interview.interviewedAtEgt] 面试时间大于
   * @param [queryInterviewReflectionsQuery.interview.interviewedAtElt] 面试时间小于
   * @param [queryInterviewReflectionsQuery.sort]
   * @param [queryInterviewReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryInterviewReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryInterviewReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryInterviewReflections(
    id: number,
    queryInterviewReflectionsQuery?: QueryInterviewReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<TaskReflection[]> {
    return axios(
      interviewApi.queryInterviewReflectionsConfig(
        id,
        queryInterviewReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryInterviewReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: QueryInterviewReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<TaskReflection[]> {
    return useQueryApi<TaskReflection[]>(
      interviewApi.queryInterviewReflections,
      'Interview.queryInterviewReflections',
      queryOptions,
      id,
      query
    )
  },

  queryExportedInterviewReflectionsConfig(
    idIn: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/interview/activity-tasks/{id}/reflections/export',
      {
        idIn,
        id,
      }
    )
  },

  /**
   * @summary 面试数据-列表导出
   * @param idIn
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedInterviewReflections(
    idIn: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      interviewApi.queryExportedInterviewReflectionsConfig(idIn, id, options)
    ) as any
  },

  useQueryExportedInterviewReflections(
    {
      idIn,
      id,
    }: {
      idIn: string
      id: number
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      interviewApi.queryExportedInterviewReflections,
      'Interview.queryExportedInterviewReflections',
      queryOptions,
      idIn,
      id
    )
  },

  submitInterviewActivityTaskConfig(
    id: number,
    interviewSubmitReq: InterviewSubmitReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/interview/task_reflection/{id}/submit',
      {
        id,
      },
      null,
      interviewSubmitReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          interviewDescription: (v: any) => v || '',
        },
      }
    )
  },

  /**
   * @summary 【后台】提交面试结果记录
   * @param id
   * @param interviewSubmitReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  submitInterviewActivityTask(
    id: number,
    interviewSubmitReq: InterviewSubmitReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      interviewApi.submitInterviewActivityTaskConfig(
        id,
        interviewSubmitReq,
        options
      )
    ) as any
  },

  useSubmitInterviewActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: InterviewSubmitReq }) =>
        interviewApi.submitInterviewActivityTask(id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(interviewApi).forEach((api) => {
  // @ts-ignore
  interviewApi[api]._name_ = `Interview.${api}`
})
